import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { 
  ArrowLeft, 
  Globe,
  ChevronRight,
  Check,
  AlertCircle,
  User,
  Phone,
  FileText,
  DollarSign,
  MapPin,
  Shield,
  Clock
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { API } from "../App";
import NavBar from "../components/NavBar";
import EmptyState, { TrustBadge, SecurityNote, ConfirmationNote } from "../components/EmptyState";

// Provider logos configuration
const providerLogos = {
  "moneygram": { logo: "/moneygram-logo.png", name: "MoneyGram" },
  "western_union": { logo: "/westernunion-logo.png", name: "Western Union" },
};

const Remittance = ({ user }) => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1); // 1: select provider, 2: form, 3: success
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [formData, setFormData] = useState({
    reference_number: "",
    expected_amount: "",
    currency: "USD",
    sender_name: "",
    sender_country: "",
    sender_phone: "",
    id_type: "",
    purpose: ""
  });
  const [submittedData, setSubmittedData] = useState(null);

  // Fetch providers and options
  const { data: providerData } = useQuery({
    queryKey: ["remittanceProviders"],
    queryFn: async () => {
      const response = await axios.get(`${API}/remittance/providers`);
      return response.data;
    }
  });

  // Submit mutation
  const submitMutation = useMutation({
    mutationFn: async () => {
      const response = await axios.post(`${API}/remittance/receive`, {
        provider: selectedProvider.provider_id,
        reference_number: formData.reference_number,
        expected_amount: parseFloat(formData.expected_amount),
        currency: formData.currency,
        sender_name: formData.sender_name,
        sender_country: formData.sender_country,
        sender_phone: formData.sender_phone,
        id_type: formData.id_type,
        purpose: formData.purpose
      });
      return response.data;
    },
    onSuccess: (data) => {
      setSubmittedData(data);
      setStep(3);
      toast.success("Remittance submitted successfully");
    },
    onError: (error) => {
      toast.error(error.response?.data?.detail || "Failed to submit remittance");
    }
  });

  const validateReferenceNumber = () => {
    const ref = formData.reference_number.replace(/\s|-/g, "");
    const expectedLength = selectedProvider?.provider_id === "western_union" ? 10 : 8;
    return ref.length === expectedLength && /^\d+$/.test(ref);
  };

  const handleSubmit = () => {
    // Validate reference number
    if (!validateReferenceNumber()) {
      const label = selectedProvider?.provider_id === "western_union" ? "MTCN" : "Reference";
      const length = selectedProvider?.provider_id === "western_union" ? 10 : 8;
      toast.error(`Invalid ${label} Number. Must be ${length} digits.`);
      return;
    }

    // Validate amount
    if (!formData.expected_amount || parseFloat(formData.expected_amount) <= 0) {
      toast.error("Please enter a valid amount");
      return;
    }

    // Validate required fields
    if (!formData.sender_name || !formData.sender_country || !formData.id_type || !formData.purpose) {
      toast.error("Please fill in all required fields");
      return;
    }

    submitMutation.mutate();
  };

  const formatCurrency = (amount, currency = "USD") => {
    const symbols = { USD: "$", JMD: "J$", GBP: "£", CAD: "C$" };
    return `${symbols[currency] || ""}${parseFloat(amount).toLocaleString("en-US", { minimumFractionDigits: 2 })}`;
  };

  return (
    <div className="min-h-screen bg-background pb-24" data-testid="remittance-page">
      <header className="px-5 pt-4 pb-2 flex items-center gap-4 max-w-lg mx-auto">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => {
            if (step === 2) setStep(1);
            else if (step === 3) navigate("/dashboard");
            else navigate("/dashboard");
          }}
          className="text-foreground"
          data-testid="back-btn"
        >
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <h1 className="font-heading font-semibold text-xl">
          {step === 3 ? "Submission Complete" : "Receive Remittance"}
        </h1>
      </header>

      <main className="px-5 max-w-lg mx-auto">
        {/* Step 1: Select Provider */}
        {step === 1 && (
          <div className="space-y-6 animate-slide-up">
            <p className="text-sm text-muted-foreground">
              Select your remittance provider to receive funds
            </p>

            <SecurityNote text="Your information is encrypted and securely transmitted." />

            <div className="space-y-3">
              {providerData?.providers?.map((provider) => {
                const logoConfig = providerLogos[provider.provider_id];
                return (
                  <button
                    key={provider.provider_id}
                    onClick={() => {
                      setSelectedProvider(provider);
                      setStep(2);
                    }}
                    className="w-full glass rounded-2xl p-5 flex items-center justify-between hover:bg-white/5 transition-colors"
                    data-testid={`provider-${provider.provider_id}`}
                  >
                    <div className="flex items-center gap-4">
                      {logoConfig ? (
                        <div className="w-12 h-12 rounded-xl bg-white border border-white/20 flex items-center justify-center p-1.5 overflow-hidden">
                          <img 
                            src={logoConfig.logo} 
                            alt={logoConfig.name} 
                            className="w-full h-full object-contain"
                          />
                        </div>
                      ) : (
                        <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                          <Globe className="w-6 h-6 text-primary" />
                        </div>
                      )}
                      <div className="text-left">
                        <p className="font-medium">{provider.name}</p>
                        <p className="text-xs text-muted-foreground">
                          {provider.reference_label}: {provider.reference_length} digits
                        </p>
                      </div>
                    </div>
                    <ChevronRight className="w-5 h-5 text-muted-foreground" />
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Step 2: Form */}
        {step === 2 && selectedProvider && (
          <div className="space-y-6 animate-slide-up">
            <div className="glass rounded-2xl p-4 flex items-center gap-3">
              {providerLogos[selectedProvider.provider_id] ? (
                <div className="w-10 h-10 rounded-xl bg-white border border-white/20 flex items-center justify-center p-1 overflow-hidden">
                  <img 
                    src={providerLogos[selectedProvider.provider_id].logo} 
                    alt={selectedProvider.name} 
                    className="w-full h-full object-contain"
                  />
                </div>
              ) : (
                <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                  <Globe className="w-5 h-5 text-primary" />
                </div>
              )}
              <div>
                <p className="font-medium">{selectedProvider.name}</p>
                <p className="text-xs text-muted-foreground">Receive Money</p>
              </div>
            </div>

            <div className="glass rounded-2xl p-5 space-y-5">
              {/* Reference Number */}
              <div>
                <Label className="flex items-center gap-2">
                  <FileText className="w-4 h-4" />
                  {selectedProvider.reference_label} *
                </Label>
                <Input
                  placeholder={`Enter ${selectedProvider.reference_length}-digit number`}
                  className="input-glass mt-2 font-mono"
                  value={formData.reference_number}
                  onChange={(e) => setFormData({ ...formData, reference_number: e.target.value })}
                  maxLength={selectedProvider.reference_length + 2}
                  data-testid="reference-input"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  {selectedProvider.reference_length} digits required
                </p>
              </div>

              {/* Expected Amount */}
              <div>
                <Label className="flex items-center gap-2">
                  <DollarSign className="w-4 h-4" />
                  Expected Amount *
                </Label>
                <div className="flex gap-2 mt-2">
                  <Input
                    type="number"
                    placeholder="0.00"
                    className="input-glass font-mono flex-1"
                    value={formData.expected_amount}
                    onChange={(e) => setFormData({ ...formData, expected_amount: e.target.value })}
                    data-testid="amount-input"
                  />
                  <select
                    className="input-glass px-3 rounded-lg bg-background border border-white/10"
                    value={formData.currency}
                    onChange={(e) => setFormData({ ...formData, currency: e.target.value })}
                    data-testid="currency-select"
                  >
                    <option value="USD">USD</option>
                    <option value="GBP">GBP</option>
                    <option value="CAD">CAD</option>
                  </select>
                </div>
              </div>

              {/* Sender Name */}
              <div>
                <Label className="flex items-center gap-2">
                  <User className="w-4 h-4" />
                  Sender Full Name *
                </Label>
                <Input
                  placeholder="Enter sender's full name"
                  className="input-glass mt-2"
                  value={formData.sender_name}
                  onChange={(e) => setFormData({ ...formData, sender_name: e.target.value })}
                  data-testid="sender-name-input"
                />
              </div>

              {/* Sender Country */}
              <div>
                <Label className="flex items-center gap-2">
                  <MapPin className="w-4 h-4" />
                  Sender Country *
                </Label>
                <select
                  className="w-full input-glass px-4 py-3 rounded-lg bg-background border border-white/10 mt-2"
                  value={formData.sender_country}
                  onChange={(e) => setFormData({ ...formData, sender_country: e.target.value })}
                  data-testid="country-select"
                >
                  <option value="">Select country</option>
                  {providerData?.sender_countries?.map((country) => (
                    <option key={country} value={country}>{country}</option>
                  ))}
                </select>
              </div>

              {/* Sender Phone */}
              <div>
                <Label className="flex items-center gap-2">
                  <Phone className="w-4 h-4" />
                  Sender Phone (optional)
                </Label>
                <Input
                  placeholder="Enter sender's phone number"
                  className="input-glass mt-2"
                  value={formData.sender_phone}
                  onChange={(e) => setFormData({ ...formData, sender_phone: e.target.value })}
                  data-testid="sender-phone-input"
                />
              </div>

              {/* ID Type */}
              <div>
                <Label>ID Type *</Label>
                <select
                  className="w-full input-glass px-4 py-3 rounded-lg bg-background border border-white/10 mt-2"
                  value={formData.id_type}
                  onChange={(e) => setFormData({ ...formData, id_type: e.target.value })}
                  data-testid="id-type-select"
                >
                  <option value="">Select ID type</option>
                  {providerData?.id_types?.map((type) => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
              </div>

              {/* Purpose */}
              <div>
                <Label>Purpose *</Label>
                <select
                  className="w-full input-glass px-4 py-3 rounded-lg bg-background border border-white/10 mt-2"
                  value={formData.purpose}
                  onChange={(e) => setFormData({ ...formData, purpose: e.target.value })}
                  data-testid="purpose-select"
                >
                  <option value="">Select purpose</option>
                  {providerData?.purposes?.map((purpose) => (
                    <option key={purpose} value={purpose}>{purpose}</option>
                  ))}
                </select>
              </div>
            </div>

            <Button
              className="w-full btn-primary h-14"
              onClick={handleSubmit}
              disabled={submitMutation.isPending}
              data-testid="submit-btn"
            >
              {submitMutation.isPending ? "Submitting..." : "Submit Request"}
            </Button>
          </div>
        )}

        {/* Step 3: Success */}
        {step === 3 && submittedData && (
          <div className="text-center animate-slide-up">
            <div className="glass rounded-3xl p-8 mb-6">
              <div className="w-20 h-20 rounded-full bg-primary/20 flex items-center justify-center mx-auto mb-6 animate-pulse-glow">
                <Check className="w-10 h-10 text-primary" />
              </div>
              
              <h2 className="font-heading font-bold text-2xl mb-2">Request Submitted!</h2>
              <p className="text-muted-foreground mb-6">
                Your remittance receive request has been submitted for verification.
              </p>

              <div className="glass rounded-xl p-4 mb-4 text-left">
                <div className="flex items-center gap-2 mb-3">
                  <AlertCircle className="w-4 h-4 text-yellow-400" />
                  <span className="text-sm text-yellow-400 font-medium">Pending Verification</span>
                </div>
                <p className="text-sm text-muted-foreground">
                  Funds will be credited to your wallet after verification is complete. This typically takes 1-2 business hours.
                </p>
              </div>

              <div className="space-y-3 text-left">
                <div className="flex justify-between py-3 border-b border-white/10">
                  <span className="text-muted-foreground">Provider</span>
                  <span className="font-medium">{selectedProvider?.name}</span>
                </div>
                <div className="flex justify-between py-3 border-b border-white/10">
                  <span className="text-muted-foreground">Reference</span>
                  <span className="font-mono">{submittedData.reference_number}</span>
                </div>
                <div className="flex justify-between py-3 border-b border-white/10">
                  <span className="text-muted-foreground">Amount</span>
                  <span className="font-mono font-medium text-primary">
                    {formatCurrency(submittedData.expected_amount, submittedData.currency)}
                  </span>
                </div>
                <div className="flex justify-between py-3 border-b border-white/10">
                  <span className="text-muted-foreground">Sender</span>
                  <span>{submittedData.sender_name}</span>
                </div>
                <div className="flex justify-between py-3">
                  <span className="text-muted-foreground">Request ID</span>
                  <span className="font-mono text-sm">{submittedData.remittance_id}</span>
                </div>
              </div>
            </div>

            <Button
              className="w-full btn-primary h-14"
              onClick={() => navigate("/dashboard")}
              data-testid="done-btn"
            >
              Done
            </Button>
          </div>
        )}
      </main>

      <NavBar active="wallet" />
    </div>
  );
};

export default Remittance;
